import { animateTo, getAnimation, setDefaultAnimation, stopAnimations } from '../../common/animation-common.js';
import { classMap } from 'lit/directives/class-map.js';
import { defaultValue } from '../../common/default-value.js';
import { HasSlotController } from '../../common/slot-controller.js';
import { html } from 'lit';
import { live } from 'lit/directives/live.js';
import { monitor } from '../../common/monitor.js';
import { property, query, state } from 'lit/decorators.js';
import { scrollIntoView } from '../../common/scroll.js';
import { waitForEvent } from '../../events/events.js';
import componentStyles from '../../styles/component.styles.js';
import formControlStyles from '../../styles/form-control.styles.js';
import styles from './combobox.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';
import type WebmoduleOption from '../option/option.js';
import type WebmodulePopup from '../popup/popup.js';

/** A data-driven option for the `options` property. */
export interface WebmoduleComboboxOption {
  value: string;
  text: string;
  disabled?: boolean;
}

setDefaultAnimation('combobox.show', {
  keyframes: [
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 }
  ],
  options: { duration: 100, easing: 'ease' }
});

setDefaultAnimation('combobox.hide', {
  keyframes: [
    { opacity: 1, scale: 1 },
    { opacity: 0, scale: 0.9 }
  ],
  options: { duration: 100, easing: 'ease' }
});

/**
 * @summary Comboboxes combine a text input with a listbox, letting users filter and pick from a set
 * of options or - with `allow-create` - enter a brand new option on the fly.
 *
 * @slot - The listbox options. Must be `<webmodule-option>` elements. Alternatively (or in addition)
 *   supply a data array via the `options` property.
 * @slot label - The control's label. Alternatively, you can use the `label` attribute.
 * @slot prefix - Used to prepend a presentational icon or similar element to the control.
 * @slot clear-icon - An icon to use in lieu of the default clear icon.
 * @slot expand-icon - The icon to show when the control is expanded and collapsed. Rotates on open and close.
 *
 * @event webmodule-change - Emitted when the control's value changes (selection or creation).
 * @event webmodule-clear - Emitted when the control's value is cleared.
 * @event webmodule-input - Emitted when the control receives input (typing).
 * @event webmodule-create - Emitted when the user creates a new option. `event.detail.value` is the text.
 * @event webmodule-focus - Emitted when the control gains focus.
 * @event webmodule-blur - Emitted when the control loses focus.
 * @event webmodule-show - Emitted when the listbox opens.
 * @event webmodule-after-show - Emitted after the listbox opens and animations complete.
 * @event webmodule-hide - Emitted when the listbox closes.
 * @event webmodule-after-hide - Emitted after the listbox closes and animations complete.
 *
 * @csspart form-control - The form control that wraps the label, input, and help text.
 * @csspart form-control-label - The label's wrapper.
 * @csspart form-control-input - The control's wrapper.
 * @csspart combobox - The container that wraps the prefix, input, clear icon, and expand button.
 * @csspart prefix - The container that wraps the prefix slot.
 * @csspart display-input - The editable `<input>` element.
 * @csspart listbox - The listbox container where options are slotted.
 * @csspart clear-button - The clear button.
 * @csspart expand-icon - The container that wraps the expand icon.
 * @csspart create-option - The "Create ..." row shown when `allow-create` is set.
 *
 * @tag webmodule-combobox
 */
export default class WebmoduleCombobox extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, formControlStyles, styles];

  @query('.combobox') popup: WebmodulePopup;
  @query('.combobox__box') box: HTMLElement;
  @query('.combobox__display-input') displayInput: HTMLInputElement;
  @query('.combobox__listbox') listbox: HTMLElement;

  /** the option currently highlighted via keyboard/hover (not necessarily selected). */
  @state() private currentOption: WebmoduleOption | null = null;
  @state() private selectedOption: WebmoduleOption | null = null;
  @state() private hasFocus = false;
  /** text shown for the committed selection, used to revert the input on blur/escape. */
  @state() private displayLabel = '';
  /** the live text in the input; doubles as the filter query while the user is typing. */
  @state() private editValue = '';
  /** true while the user is actively filtering (typing), so a fresh open shows everything. */
  @state() private filtering = false;
  /** true when the "Create ..." row is the highlighted item. */
  @state() private createCurrent = false;
  @state() private hasVisibleOptions = true;

  /** The name of the control. */
  @property() name = '';
  /** The control's value - the selected option's `value`, or the text of a created option. */
  @property() value = '';
  /** The default value of the control. Primarily used for resetting. */
  @defaultValue() defaultValue = '';
  /** The control's size. */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' = 'medium';
  /** Placeholder text shown when the control is empty. */
  @property() placeholder = '';
  /** Disables the control. */
  @property({ type: Boolean, reflect: true }) disabled = false;
  /** Adds a clear button when the control is not empty. */
  @property({ type: Boolean }) clearable = false;
  /** When set, lets the user create a new option by typing a value that matches none of the options. */
  @property({ attribute: 'allow-create', type: Boolean, reflect: true }) allowCreate = false;
  /** Indicates whether the listbox is open. */
  @property({ type: Boolean, reflect: true }) open = false;
  /** Prevents the listbox from being clipped when placed inside a container with `overflow: auto|scroll`. */
  @property({ type: Boolean }) hoist = false;
  /** Draws a filled control. */
  @property({ type: Boolean, reflect: true }) filled = false;
  /** Draws a pill-style control with rounded edges. */
  @property({ type: Boolean, reflect: true }) pill = false;
  /** The control's label. If you need to display HTML, use the `label` slot instead. */
  @property() label = '';
  /** The preferred placement of the listbox. */
  @property({ reflect: true }) placement: 'top' | 'bottom' = 'bottom';
  /** The control's required attribute. */
  @property({ type: Boolean, reflect: true }) required = false;
  /**
   * A data-driven alternative to slotted options. When set, the component materialises matching
   * `<webmodule-option>` children. Authored (slotted) options are preserved alongside these.
   *
   * Note: changing the *content* of this array rebuilds the generated options, which also removes
   * any option the user created via `allow-create` (an unchanged array is left intact, so a created
   * option survives re-renders). Consumers that allow creation should fold a newly created value
   * (from `webmodule-create`) into this array when they next update it.
   */
  @property({ attribute: false }) options: WebmoduleComboboxOption[] | null = null;
  /** Customises the text of the "Create ..." row. */
  @property({ attribute: false }) getCreateLabel: (value: string) => string = value => `Create "${value}"`;
  /** Text shown in the listbox when there are no matching options (and nothing to create). */
  @property({ attribute: 'empty-text' }) emptyText = 'No results';

  private readonly hasSlotController = new HasSlotController(this, 'label');
  private closeWatcher: CloseWatcher | null;
  /** content key of the last `options` array materialised, to avoid needless rebuilds. */
  private generatedOptionsKey: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.open = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeOpenListeners();
  }

  @monitor('disabled', { delayMonitorUntilFirstUpdate: true })
  handleDisabledChange() {
    if (this.disabled) {
      this.open = false;
      this.handleOpenChange();
    }
  }

  @monitor('value', { delayMonitorUntilFirstUpdate: true })
  handleValueChange() {
    this.selectByValue(this.value);
  }

  @monitor('options')
  handleOptionsChange() {
    this.syncGeneratedOptions();
  }

  @monitor('open', { delayMonitorUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.open && !this.disabled) {
      // a fresh open (not via typing) shows every option
      if (!this.filtering) {
        this.editValue = this.displayLabel;
        this.applyFilter();
      }
      this.setCurrentOption(this.selectedOption ?? this.getFirstVisibleOption());

      this.emit('webmodule-show');
      this.addOpenListeners();

      await stopAnimations(this);
      this.listbox.hidden = false;
      this.popup.active = true;

      requestAnimationFrame(() => this.setCurrentOption(this.currentOption));

      const { keyframes, options } = getAnimation(this, 'combobox.show');
      await animateTo(this.popup.popup, keyframes, options);

      if (this.currentOption) {
        scrollIntoView(this.currentOption, this.listbox, 'vertical', 'auto');
      }

      this.emit('webmodule-after-show');
    } else {
      this.emit('webmodule-hide');
      this.removeOpenListeners();

      await stopAnimations(this);
      const { keyframes, options } = getAnimation(this, 'combobox.hide');
      await animateTo(this.popup.popup, keyframes, options);
      this.listbox.hidden = true;
      this.popup.active = false;

      this.emit('webmodule-after-hide');
    }
  }

  protected firstUpdated() {
    this.syncGeneratedOptions();
    this.selectByValue(this.value);
  }

  /** Shows the listbox. */
  async show() {
    if (this.open || this.disabled) {
      this.open = false;
      return undefined;
    }
    this.open = true;
    return waitForEvent(this, 'webmodule-after-show');
  }

  /** Hides the listbox. */
  async hide() {
    if (!this.open || this.disabled) {
      this.open = false;
      return undefined;
    }
    this.open = false;
    return waitForEvent(this, 'webmodule-after-hide');
  }

  focus(options?: FocusOptions) {
    this.displayInput.focus(options);
  }

  blur() {
    this.displayInput.blur();
  }

  // --- option helpers --------------------------------------------------------

  /** All `<webmodule-option>` children (authored + generated + created). */
  private getAllOptions(): WebmoduleOption[] {
    return [...this.querySelectorAll<WebmoduleOption>('webmodule-option')];
  }

  /** Options currently visible (not filtered out). */
  private getVisibleOptions(): WebmoduleOption[] {
    return this.getAllOptions().filter(o => !o.hidden);
  }

  private getFirstVisibleOption(): WebmoduleOption | null {
    return this.getVisibleOptions()[0] ?? null;
  }

  /**
   * Materialise the `options` data array as managed `<webmodule-option>` children. Guarded by a
   * content key so a re-render that passes an equal-but-new `options` array does NOT rebuild the
   * DOM - that would wipe an option the user just created via the "Create ..." row. Rebuilding only
   * happens when the option data actually changes.
   */
  private syncGeneratedOptions() {
    const key = this.options ? JSON.stringify(this.options.map(o => [o.value, o.text, !!o.disabled])) : '';
    if (key === this.generatedOptionsKey) return;
    this.generatedOptionsKey = key;

    this.querySelectorAll('webmodule-option[data-combobox-generated]').forEach(el => el.remove());

    if (this.options?.length) {
      const fragment = document.createDocumentFragment();
      for (const option of this.options) {
        const el = document.createElement('webmodule-option');
        el.setAttribute('data-combobox-generated', '');
        el.value = option.value;
        if (option.disabled) el.disabled = true;
        el.textContent = option.text;
        fragment.appendChild(el);
      }
      this.appendChild(fragment);
    }

    // reflect the current value/label against the refreshed option set
    this.selectByValue(this.value);
  }

  // --- selection -------------------------------------------------------------

  /** Select the option whose `value` matches, updating value/label. Clears selection when no match. */
  private selectByValue(value: string) {
    const allOptions = this.getAllOptions();
    if (!customElements.get('webmodule-option')) {
      customElements.whenDefined('webmodule-option').then(() => this.selectByValue(this.value));
      return;
    }

    const match = allOptions.find(o => o.value === value) ?? null;
    allOptions.forEach(o => (o.selected = o === match));
    this.selectedOption = match;
    this.displayLabel = match?.getTextLabel() ?? (value || '');
    if (!this.hasFocus) this.editValue = this.displayLabel;
  }

  /** Commit a selection (from the list) and emit change. */
  private commitOption(option: WebmoduleOption) {
    const previous = this.value;
    this.getAllOptions().forEach(o => (o.selected = o === option));
    this.selectedOption = option;
    this.value = option.value;
    this.displayLabel = option.getTextLabel();
    this.editValue = this.displayLabel;
    this.filtering = false;

    this.updateComplete.then(() => {
      this.displayInput.focus({ preventScroll: true });
      if (this.value !== previous) {
        this.emit('webmodule-input');
        this.emit('webmodule-change');
      }
    });

    this.hide();
  }

  /** Create a new option from the given text (or select an existing match) and commit it. */
  private createOption(rawText: string) {
    const text = rawText.trim();
    if (!text) return;

    const existing = this.getAllOptions().find(o => o.getTextLabel().trim().toLowerCase() === text.toLowerCase());
    let option = existing;

    if (!option) {
      option = document.createElement('webmodule-option');
      option.setAttribute('data-combobox-generated', '');
      option.value = text;
      option.textContent = text;
      this.appendChild(option);
      this.emit('webmodule-create', { detail: { value: text } });
    }

    this.createCurrent = false;
    this.commitOption(option);
  }

  // --- filtering -------------------------------------------------------------

  private applyFilter() {
    const queryText = this.filtering ? this.editValue.trim().toLowerCase() : '';
    let firstVisible: WebmoduleOption | null = null;

    for (const option of this.getAllOptions()) {
      const matches = queryText === '' || option.getTextLabel().toLowerCase().includes(queryText);
      option.hidden = !matches;
      if (matches && !firstVisible) firstVisible = option;
    }

    this.hasVisibleOptions = firstVisible !== null;
  }

  /** Whether the "Create ..." row should be offered for the current query. */
  private get showCreateRow(): boolean {
    if (!this.allowCreate || !this.filtering) return false;
    const queryText = this.editValue.trim();
    if (queryText === '') return false;
    return !this.getAllOptions().some(o => o.getTextLabel().trim().toLowerCase() === queryText.toLowerCase());
  }

  // --- listeners -------------------------------------------------------------

  private addOpenListeners() {
    document.addEventListener('focusin', this.handleDocumentFocusIn);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    document.addEventListener('mousedown', this.handleDocumentMouseDown);

    if (this.getRootNode() !== document) {
      this.getRootNode().addEventListener('focusin', this.handleDocumentFocusIn);
    }

    if ('CloseWatcher' in window) {
      this.closeWatcher?.destroy();
      this.closeWatcher = new CloseWatcher();
      this.closeWatcher.onclose = () => {
        if (this.open) {
          // match the non-CloseWatcher Escape path: discard the uncommitted query before closing
          this.revertToSelection();
          this.hide();
          this.displayInput.focus({ preventScroll: true });
        }
      };
    }
  }

  private removeOpenListeners() {
    document.removeEventListener('focusin', this.handleDocumentFocusIn);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);

    if (this.getRootNode() !== document) {
      this.getRootNode().removeEventListener('focusin', this.handleDocumentFocusIn);
    }

    this.closeWatcher?.destroy();
  }

  private handleDocumentFocusIn = (event: FocusEvent) => {
    const path = event.composedPath();
    if (this && !path.includes(this)) this.hide();
  };

  private handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath();
    if (this && !path.includes(this)) this.hide();
  };

  private handleDocumentKeyDown = (event: KeyboardEvent) => {
    // Close on escape
    if (event.key === 'Escape' && this.open && !this.closeWatcher) {
      event.preventDefault();
      event.stopPropagation();
      this.revertToSelection();
      this.hide();
      this.displayInput.focus({ preventScroll: true });
    }
  };

  // --- input / keyboard ------------------------------------------------------

  private handleFocus() {
    this.hasFocus = true;
    // start fresh: typing filters, and the whole text is selected for quick replacement
    this.displayInput.select();
    this.emit('webmodule-focus');
  }

  private handleBlur() {
    this.hasFocus = false;
    // discard an uncommitted query and restore the selected label
    this.revertToSelection();
    this.emit('webmodule-blur');
  }

  private revertToSelection() {
    this.filtering = false;
    this.editValue = this.displayLabel;
    this.createCurrent = false;
    this.applyFilter();
  }

  private handleInput() {
    this.editValue = this.displayInput.value;
    this.filtering = true;
    this.createCurrent = false;

    if (!this.open) this.open = true;

    this.applyFilter();
    this.setCurrentOption(this.getFirstVisibleOption());
    if (!this.currentOption && this.showCreateRow) this.createCurrent = true;

    this.emit('webmodule-input');
  }

  private handleBoxMouseDown(event: MouseEvent) {
    const path = event.composedPath();
    const isClear = path.some(el => el instanceof Element && el.classList?.contains('combobox__clear'));
    if (this.disabled || isClear) return;

    // open without stealing the caret from the input
    if (!this.open) {
      this.filtering = false;
      this.open = true;
    }
  }

  private handleComboboxKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.open) {
        this.show();
        return;
      }
      if (this.createCurrent) {
        this.createOption(this.editValue);
      } else if (this.currentOption && !this.currentOption.disabled) {
        this.commitOption(this.currentOption);
      }
      return;
    }

    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.open) {
        this.show();
        return;
      }
      this.moveSelection(event.key);
    }
  }

  /** Navigate the visible options, with the "Create ..." row as a virtual last item. */
  private moveSelection(key: string) {
    const options = this.getVisibleOptions();
    const items: (WebmoduleOption | 'create')[] = this.showCreateRow ? [...options, 'create'] : [...options];
    if (items.length === 0) return;

    const currentItem: WebmoduleOption | 'create' | null = this.createCurrent ? 'create' : this.currentOption;
    const currentIndex = currentItem ? items.indexOf(currentItem) : -1;
    let newIndex = Math.max(0, currentIndex);

    if (key === 'ArrowDown') newIndex = currentIndex + 1 > items.length - 1 ? 0 : currentIndex + 1;
    else if (key === 'ArrowUp') newIndex = currentIndex - 1 < 0 ? items.length - 1 : currentIndex - 1;
    else if (key === 'Home') newIndex = 0;
    else if (key === 'End') newIndex = items.length - 1;

    const next = items[newIndex];
    if (next === 'create') {
      this.setCurrentOption(null);
      this.createCurrent = true;
    } else {
      this.createCurrent = false;
      this.setCurrentOption(next);
    }
  }

  private setCurrentOption(option: WebmoduleOption | null) {
    this.getAllOptions().forEach(el => {
      el.current = false;
      el.tabIndex = -1;
    });

    this.currentOption = option;
    if (option) {
      option.current = true;
      option.tabIndex = 0;
      scrollIntoView(option, this.listbox, 'vertical', 'auto');
    }
  }

  private handleLabelClick() {
    this.displayInput.focus();
  }

  private handleListboxMouseDown(event: MouseEvent) {
    // keep focus (and the caret) in the input while clicking options / the create row
    event.preventDefault();
  }

  private handleOptionClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const option = target.closest<WebmoduleOption>('webmodule-option');
    if (option && !option.disabled && !option.hidden) {
      this.commitOption(option);
    }
  }

  private handleCreateClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.createOption(this.editValue);
  }

  private handleClearClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.value !== '') {
      this.value = '';
      this.selectByValue('');
      this.editValue = '';
      this.displayInput.focus({ preventScroll: true });
      this.updateComplete.then(() => {
        this.emit('webmodule-clear');
        this.emit('webmodule-input');
        this.emit('webmodule-change');
      });
    }
  }

  private handleDefaultSlotChange() {
    if (!customElements.get('webmodule-option')) {
      customElements.whenDefined('webmodule-option').then(() => this.handleDefaultSlotChange());
      return;
    }
    this.selectByValue(this.value);
    this.applyFilter();
  }

  render() {
    const hasLabelSlot = this.hasSlotController.checkFor('label');
    const hasLabel = this.label ? true : !!hasLabelSlot;
    const hasClearIcon = this.clearable && !this.disabled && this.value?.length > 0;

    return html`
      <div
        part="form-control"
        class=${classMap({
          'form-control': true,
          'form-control--small': this.size === 'small',
          'form-control--medium': this.size === 'medium',
          'form-control--large': this.size === 'large',
          'form-control--has-label': hasLabel
        })}
      >
        <label
          id="label"
          part="form-control-label"
          class="form-control__label"
          aria-hidden=${hasLabel ? 'false' : 'true'}
          @click=${this.handleLabelClick}
        >
          <slot name="label">${this.label}</slot>
        </label>

        <div part="form-control-input" class="form-control-input">
          <webmodule-popup
            class=${classMap({
              combobox: true,
              'combobox--standard': !this.filled,
              'combobox--filled': this.filled,
              'combobox--pill': this.pill,
              'combobox--open': this.open,
              'combobox--disabled': this.disabled,
              'combobox--focused': this.hasFocus,
              'combobox--top': this.placement === 'top',
              'combobox--bottom': this.placement === 'bottom',
              'combobox--small': this.size === 'small',
              'combobox--medium': this.size === 'medium',
              'combobox--large': this.size === 'large'
            })}
            placement=${this.placement}
            strategy=${this.hoist ? 'fixed' : 'absolute'}
            flip
            shift
            sync="width"
            auto-size="vertical"
            auto-size-padding="10"
          >
            <div part="combobox" class="combobox__box" slot="anchor" @mousedown=${this.handleBoxMouseDown}>
              <slot part="prefix" name="prefix" class="combobox__prefix"></slot>

              <input
                part="display-input"
                class="combobox__display-input"
                type="text"
                placeholder=${this.placeholder}
                .disabled=${this.disabled}
                .value=${live(this.editValue)}
                autocomplete="off"
                spellcheck="false"
                autocapitalize="off"
                role="combobox"
                aria-controls="listbox"
                aria-expanded=${this.open ? 'true' : 'false'}
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-labelledby="label"
                aria-disabled=${this.disabled ? 'true' : 'false'}
                tabindex="0"
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}
                @input=${this.handleInput}
                @keydown=${this.handleComboboxKeyDown}
              />

              ${hasClearIcon
                ? html`
                    <button
                      part="clear-button"
                      class="combobox__clear"
                      type="button"
                      aria-label="Clear control"
                      @mousedown=${(e: MouseEvent) => e.preventDefault()}
                      @click=${this.handleClearClick}
                      tabindex="-1"
                    >
                      <slot name="clear-icon">
                        <webmodule-icon name="x-circle-fill"></webmodule-icon>
                      </slot>
                    </button>
                  `
                : ''}

              <slot name="expand-icon" part="expand-icon" class="combobox__expand-icon">
                <webmodule-icon name="chevron-down"></webmodule-icon>
              </slot>
            </div>

            <div
              id="listbox"
              role="listbox"
              aria-labelledby="label"
              part="listbox"
              class="combobox__listbox"
              tabindex="-1"
              @mousedown=${this.handleListboxMouseDown}
              @mouseup=${this.handleOptionClick}
              @slotchange=${this.handleDefaultSlotChange}
            >
              <slot></slot>

              ${this.showCreateRow
                ? html`
                    <div
                      part="create-option"
                      class=${classMap({
                        combobox__create: true,
                        'combobox__create--current': this.createCurrent
                      })}
                      role="option"
                      aria-selected=${this.createCurrent ? 'true' : 'false'}
                      @mouseenter=${() => {
                        this.createCurrent = true;
                        this.setCurrentOption(null);
                      }}
                      @mouseleave=${() => (this.createCurrent = false)}
                      @click=${this.handleCreateClick}
                    >
                      <webmodule-icon name="plus" aria-hidden="true"></webmodule-icon>
                      <span class="combobox__create-text">${this.getCreateLabel(this.editValue.trim())}</span>
                    </div>
                  `
                : ''}
              ${!this.hasVisibleOptions && !this.showCreateRow
                ? html`<div class="combobox__empty">${this.emptyText}</div>`
                : ''}
            </div>
          </webmodule-popup>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-combobox': WebmoduleCombobox;
  }
}
